import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import CSSModules from 'react-css-modules';
import isSameDay from 'date-fns-old/is_same_day';
import format from 'date-fns-old/format';
import browserHistory from 'react-router/lib/browserHistory';
import { injectIntl } from 'react-intl';
import { get } from 'lodash';

import { actions as acceptJoinRequest } from '../../../../redux/communities/acceptJoinRequest';
import { actions as declineJoinRequest } from '../../../../redux/communities/declineJoinRequest';
import { actions as declineInvitation } from '../../../../redux/communities/declineInvitation';
import { actions as acceptInvitation } from '../../../../redux/communities/acceptInvitation';
import { COMMUNITIES, PUBLIC_PROFILE } from '../../../../constants/routes';
import { adaptDateToLocalTimezone } from '../../../../utils/converters/dateConverters';
import MessageSeparatorView from '../../../Chat/components/MessageSeparatorView';
import NotificationListItem from './NotificationListItem';
import ActivityListItem from './ActivityListItem';
import InviteListItem from './InviteListItem';
import { ACTIVITY_TYPES } from '../../../../constants';
import { COMMON } from '../../../../constants/strings';
import styles from './index.styl';

const TIMESTAMP_FORMATTING_PATTERN = 'h:mm a';

const mapDispatchToProps = {
  acceptCommunityJoinRequest: acceptJoinRequest.send,
  declineCommunityJoinRequest: declineJoinRequest.send,
  acceptInvite: declineInvitation.send,
  declineInvite: acceptInvitation.send,
};

@injectIntl
@connect(
  null,
  mapDispatchToProps,
)
@CSSModules(styles, { allowMultiple: true })
export default class ActivitiesList extends Component {
  static propTypes = {
    acceptCommunityJoinRequest: PropTypes.func,
    declineCommunityJoinRequest: PropTypes.func,
    acceptInvite: PropTypes.func,
    declineInvite: PropTypes.func,
    list: PropTypes.arrayOf(PropTypes.object).isRequired,
    className: PropTypes.string,
    listType: PropTypes.string.isRequired,
    intl: PropTypes.shape({
      formatMessage: PropTypes.func,
    }).isRequired,
  };

  static defaultProps = {
    className: '',
    acceptCommunityJoinRequest: null,
    declineCommunityJoinRequest: null,
  };

  isNewDayForMessage = (list, index) => {
    if (index === 0) return true;

    const previousTime = adaptDateToLocalTimezone(list[index - 1].createdAt);
    const nextTime = adaptDateToLocalTimezone(list[index].createdAt);
    return !isSameDay(previousTime, nextTime);
  };

  convertItemTime = createdAt => {
    const date = adaptDateToLocalTimezone(createdAt);
    return format(date, TIMESTAMP_FORMATTING_PATTERN);
  };

  handleJoinRequest = (id, type) => {
    const {
      acceptCommunityJoinRequest,
      declineCommunityJoinRequest,
    } = this.props;
    return type === 'accept'
      ? acceptCommunityJoinRequest({ id, type })
      : declineCommunityJoinRequest({ id, type });
  };

  acceptCommunityInvite = id => {
    const { acceptInvite } = this.props;

    acceptInvite({ id, actionType: 'accept' });
  };
  declineCommunityInvite = id => {
    const { declineInvite } = this.props;

    declineInvite({ id, actionType: 'decline' });
  };

  handleOpen = (id, routeType) => {
    const route =
      routeType === 'community'
        ? `/${COMMUNITIES}/${id}`
        : `/${PUBLIC_PROFILE}/${id}`;
    browserHistory.push(route);
  };

  renderInviteItem = item => {
    const { subject = {}, disabled, createdAt } = item;
    const { id, community, inviter } = subject;
    const userName = get(inviter, 'username');
    const name = get(inviter, 'displayableName', userName);

    return (
      <InviteListItem
        key={id}
        logo={get(inviter, 'avatar.thumbMiddle')}
        name={name}
        userName={userName}
        role={get(inviter, 'providerRole')}
        id={get(inviter, 'id')}
        disabled={disabled}
        createdAt={createdAt}
        handleAccept={this.acceptCommunityInvite}
        handleDecline={this.declineCommunityInvite}
        invitationId={id}
        onClick={this.handleOpen}
        organizations={get(community, 'organization.name')}
        communityId={get(community, 'id')}
        subtitle={get(community, 'name')}
      />
    );
  };

  renderRequestItem = item => {
    const { subject, disabled, createdAt } = item;
    const community = get(subject, 'community');
    const user = get(subject, 'user');
    const subjectId = get(subject, 'id');
    const { name, id: communityId } = community || {};
    const {
      username,
      displayableName,
      userOrganizations = [],
      providerRole,
      avatar = {},
      id,
    } = user || {};

    return (
      <ActivityListItem
        key={subjectId}
        userName={username}
        name={displayableName || username}
        subtitle={name}
        createdAt={createdAt}
        time={this.convertItemTime(createdAt)}
        onHandleJoinRequest={this.handleJoinRequest}
        logo={avatar.url}
        organizations={userOrganizations.join(', ')}
        id={id}
        communityId={communityId}
        onClick={this.handleOpen}
        role={providerRole}
        disabled={disabled}
        requestId={subjectId}
      />
    );
  };

  renderNotificationItem = item => {
    const {
      intl: { formatMessage },
    } = this.props;
    const { id, subject, createdAt, isNew } = item;

    return (
      <NotificationListItem
        key={id}
        subject={subject}
        createdAt={createdAt}
        onClick={this.handleOpen}
        hasNewLabel={isNew}
        newLabelText={formatMessage(COMMON.new)}
      />
    );
  };

  renderItems = () => {
    const { list, listType } = this.props;

    return list.reduce((acc, item, index) => {
      const isConfirmedData =
        get(item, 'subject.user.emailConfirmed', false) ||
        get(item, 'subject.user.phoneNumberConfirmed', false);
      if (
        (this.isNewDayForMessage(list, index) &&
          listType !== ACTIVITY_TYPES.JOIN_REQUESTS) ||
        (listType === ACTIVITY_TYPES.JOIN_REQUESTS && isConfirmedData)
      ) {
        acc.push(
          <MessageSeparatorView
            key={`${item.id}-${item.createdAt}`}
            date={adaptDateToLocalTimezone(item.createdAt)}
            left
            hasBorder
          />,
        );
      }
      if (listType === ACTIVITY_TYPES.NOTIFICATIONS) {
        acc.push(this.renderNotificationItem(item));
      }

      if (listType === ACTIVITY_TYPES.JOIN_REQUESTS && isConfirmedData) {
        acc.push(this.renderRequestItem(item));
      }

      if (listType === ACTIVITY_TYPES.MEMBER_INVITATIONS) {
        acc.push(this.renderInviteItem(item));
      }

      return acc;
    }, []);
  };

  render() {
    const { list, className, listType } = this.props;

    return (
      <ul styleName="list" className={className} key={listType}>
        {Boolean(list.length) && this.renderItems()}
      </ul>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/Communities/Components/ActivitiesList/index.jsx